import React from 'react'
import { motion } from 'framer-motion'
import SEOHead from '@/components/SEO/SEOHead'
import { buildGraph, breadcrumbListData, webPageSchema } from '@/data/structured-data'
import PageHeader from '@/components/Common/PageHeader'
import { contactInfo } from '@/data/contact'
import { toTelHref, toWhatsAppHref } from '@/utils/phone'

const AboutPage: React.FC = () => {
  const telHref = toTelHref(contactInfo.phone)
  const waHref = toWhatsAppHref(contactInfo.whatsapp)
  return (
    <>
      <SEOHead
        title="Bizi Tanıyın | Ardıç Veteriner Kliniği"
        description="Şanlıurfa Karaköprü'de modern donanımlı veteriner kliniği. Ameliyathane, dijital görüntüleme, klinik laboratuvar ve 7/24 acil hizmetle kedi-köpek sağlığı."
        keywords="şanlıurfa veteriner, karaköprü veteriner, yenişehir veteriner kliniği, en iyi veteriner, hayvan sagligi, veteriner yorumları, veteriner klinigi"
        canonicalUrl="/about"
        structuredData={buildGraph(
          webPageSchema({
            name: 'Bizi Tanıyın',
            description: "Ardıç Veteriner Kliniği'nin misyonu, değerleri ve modern klinik yaklaşımı.",
            url: '/about',
            type: 'AboutPage'
          }),
          breadcrumbListData([
            { name: 'Ana Sayfa', url: '/' },
            { name: 'Bizi Tanıyın', url: '/about' }
          ])
        )}
      />
      <PageHeader title="Bizi Tanıyın" subtitle="Ardıç Veteriner Kliniği olarak can dostlarınız için modern, güvenilir ve şefkatli bakım sunuyoruz." />
      <section className="section-padding">
        <div className="container-custom grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}}>
              <h2 className="text-2xl font-bold mb-2">Misyonumuz</h2>
              <p className="text-gray-700 leading-relaxed">Can dostlarınızın sağlığını korumak ve yaşam kalitesini artırmak için güncel veteriner tıp uygulamalarını, kanıta dayalı yaklaşımla ve modern teknoloji ile birleştiriyoruz. Her hastaya bireysel ihtiyaçlarına uygun tanı ve tedavi planı sunmayı hedefliyoruz.</p>
            </motion.div>

            <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}}>
              <h2 className="text-2xl font-bold mb-2">Vizyonumuz</h2>
              <p className="text-gray-700 leading-relaxed">Bölgenin referans veteriner kliniği olarak güvenilir, ulaşılabilir ve yüksek kalitede sağlık hizmetini sürdürülebilir biçimde sunmak; eğitim ve danışmanlıkla toplumda farkındalık yaratmak.</p>
            </motion.div>

            <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}}>
              <h2 className="text-2xl font-bold mb-2">Klinik Yaklaşım</h2>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-gray-700">
                <li className="p-4 bg-white rounded-xl border">Triyaj ve önceliklendirme ile hızlı müdahale</li>
                <li className="p-4 bg-white rounded-xl border">Sterilizasyon protokolleri ve kayıtlı süreç yönetimi</li>
                <li className="p-4 bg-white rounded-xl border">Ağrı skorlaması ve multimodal analjezi</li>
                <li className="p-4 bg-white rounded-xl border">Evde bakım ve taburculuk eğitimi</li>
              </ul>
            </motion.div>

            <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}}>
              <h2 className="text-2xl font-bold mb-2">Teknoloji ve Donanım</h2>
              <p className="text-gray-700 leading-relaxed">Dijital röntgen, ultrasonografi, kapsamlı laboratuvar ve modern ameliyathane altyapımız ile tanı ve tedavi süreçlerinde hız ve doğruluk sağlıyoruz.</p>
            </motion.div>

            <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}}>
              <h2 className="text-2xl font-bold mb-2">Hijyen ve Güvenlik</h2>
              <p className="text-gray-700 leading-relaxed">HEPA filtrasyon, yüzey dezenfeksiyonu ve alet sterilizasyonu dahil olmak üzere uluslararası standartlarda hijyen protokollerini uyguluyoruz.</p>
            </motion.div>

            <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}}>
              <div className="rounded-xl border p-6 bg-primary-50">
                <h3 className="font-semibold text-primary-700 mb-2">Sorularınız mı var?</h3>
                <p className="text-primary-900 mb-4">Randevu ve bilgi için bize ulaşabilirsiniz. Ekibimiz yardımcı olmaktan memnuniyet duyar.</p>
                <div className="flex flex-wrap gap-3">
                  <a href={telHref} className="px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition">Ara</a>
                  <a href={waHref} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition">WhatsApp</a>
                </div>
              </div>
            </motion.div>
          </div>
          <div className="space-y-4">
            <img src="/klinik.webp" className="rounded-xl shadow" alt="klinik" loading="lazy" />
            <img src="/klinik.webp" className="rounded-xl shadow" alt="ekip" loading="lazy" />
          </div>
        </div>
      </section>
    </>
  )
}

export default AboutPage


